import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { setUser } from '../store/authSlice'
import authService from '../services/authService'
import { consumeInviteRedirectPath } from '../utils/inviteRedirect'
import { useTranslation } from '../hooks/use-i18n'

const AuthCallback: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [message, setMessage] = useState(t('auth.completingGoogleSignIn'))

  useEffect(() => {
    let cancelled = false

    const finish = async (token: string, user?: Awaited<ReturnType<typeof authService.getCurrentUser>>) => {
      localStorage.setItem('token', token)
      const nextUser = user || (await authService.getCurrentUser())
      if (cancelled) return
      authService.setAuthData(nextUser, token)
      dispatch(setUser(nextUser))
      toast.success(t('auth.welcomeBackToast'))
      navigate(consumeInviteRedirectPath('/'), { replace: true })
    }

    const fail = () => {
      if (cancelled) return
      authService.logout()
      toast.error(t('auth.googleSignInFailed'))
      navigate('/login', { replace: true })
    }

    const complete = async () => {
      const query = new URLSearchParams(window.location.search)
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const googleError = hash.get('error') || query.get('error')
      const googleIdToken = hash.get('id_token')?.trim()
      const backendToken = query.get('token')?.trim()

      if (googleError) {
        fail()
        return
      }

      try {
        if (googleIdToken) {
          window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
          const { token, user } = await authService.signInWithGoogleIdToken(googleIdToken)
          await finish(token, user)
          return
        }
        if (backendToken) {
          await finish(backendToken)
          return
        }
        fail()
      } catch {
        fail()
      }
    }

    complete()

    return () => {
      cancelled = true
    }
  }, [dispatch, navigate, t])

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center px-4">
      <p className="text-foreground/90 text-body">{message}</p>
    </div>
  )
}

export default AuthCallback
